import React, { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';

const CSV_TEMPLATE = `Descrição;Valor;Data Vencimento;Categoria;Data Emissão;Data Pagamento;Status;Projeto;Observações
Aluguel Escritório;2500,00;05/02/2026;Aluguel;01/02/2026;;pendente;;
Internet fibra;299,90;15/02/2026;Infraestrutura;;;pendente;;
Energia elétrica;480,50;20/02/2026;Infraestrutura;01/02/2026;18/02/2026;pago;;ref. jan/26`;

/**
 * Bulk-import expenses from CSV. Mirrors the pattern used by the budgets
 * importer — modal owns all local state; parent is only notified on
 * successful import so it can refresh its data.
 */
export default function ImportExpensesModal({ open, onOpenChange, onImported }) {
  const [csvText, setCsvText] = useState('');
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result || '');
    reader.readAsText(file, 'utf-8');
  };

  const handleImport = async () => {
    if (!csvText.trim()) {
      toast.error('Cole o conteúdo do CSV ou selecione um arquivo');
      return;
    }
    setImporting(true);
    setResult(null);
    try {
      const res = await api.post('/financial/bulk-import-expenses', {
        csv: csvText,
        skip_duplicates: skipDuplicates,
      });
      setResult(res.data);
      toast.success(
        `Importação: ${res.data.imported} despesa(s), ${res.data.skipped} pulada(s), ${res.data.errors} erro(s)`
      );
      await onImported?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao importar');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const blob = new Blob(['\ufeff' + CSV_TEMPLATE], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modelo_despesas.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    onOpenChange(false);
    setCsvText('');
    setResult(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importar Despesas via CSV</DialogTitle>
          <DialogDescription>
            Importação em lote de despesas a partir de arquivo CSV. Aceita
            separador vírgula (,) ou ponto-e-vírgula (;). Para agilizar,
            baixe o modelo abaixo.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mt-2 text-xs text-emerald-900 space-y-1">
          <p className="font-semibold mb-1">Colunas esperadas (cabeçalho na 1ª linha):</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 list-disc list-inside">
            <li><b>Descrição</b> <span className="text-emerald-700">(obrigatório)</span></li>
            <li><b>Valor</b> <span className="text-emerald-700">(obrigatório — "1234,56" ou "1234.56")</span></li>
            <li><b>Data Vencimento</b> <span className="text-emerald-700">(obrigatório — DD/MM/AAAA)</span></li>
            <li><b>Categoria</b> <span className="text-gray-600">(opcional, default "Outros")</span></li>
            <li><b>Data Emissão</b> <span className="text-gray-600">(opcional)</span></li>
            <li><b>Data Pagamento</b> <span className="text-gray-600">(opcional — se preenchido, status vira "pago")</span></li>
            <li><b>Status</b> <span className="text-gray-600">(pendente/pago/atrasado — opcional)</span></li>
            <li><b>Projeto</b> <span className="text-gray-600">(opcional — nome exato do projeto)</span></li>
            <li className="sm:col-span-2"><b>Observações</b> <span className="text-gray-600">(opcional — anexa à descrição)</span></li>
          </ul>
          <p className="mt-2 text-[11px] text-emerald-800">
            <b>Duplicatas:</b> a mesma descrição + valor + vencimento é considerada duplicata e pulada.
          </p>
        </div>

        <div className="space-y-4 mt-3">
          <div className="flex items-center justify-between">
            <Label>1) Selecione o arquivo CSV</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleDownloadTemplate}
              className="text-emerald-700"
              data-testid="btn-download-expenses-template"
            >
              ⤓ Baixar modelo
            </Button>
          </div>
          <Input
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
            className="cursor-pointer"
            data-testid="input-import-expenses-file"
          />
          <p className="text-xs text-gray-500 -mt-2">Ou cole o conteúdo abaixo:</p>

          <div>
            <Label htmlFor="expense-csv-text">2) Conteúdo CSV</Label>
            <Textarea
              id="expense-csv-text"
              rows={8}
              placeholder={CSV_TEMPLATE}
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              className="font-mono text-xs"
              data-testid="textarea-import-expenses"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={skipDuplicates}
              onChange={(e) => setSkipDuplicates(e.target.checked)}
              data-testid="checkbox-skip-expense-duplicates"
            />
            Pular duplicados
          </label>

          {result && (
            <div className="border rounded-lg p-4 bg-gray-50 space-y-2 max-h-72 overflow-y-auto">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-2xl font-bold text-emerald-700">{result.imported}</p>
                  <p className="text-xs text-gray-600">Importadas</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{result.skipped}</p>
                  <p className="text-xs text-gray-600">Puladas</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{result.errors}</p>
                  <p className="text-xs text-gray-600">Erros</p>
                </div>
              </div>

              {result.details?.errors?.length > 0 && (
                <div className="text-xs">
                  <p className="font-semibold text-red-700 mt-2">Erros:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    {result.details.errors.slice(0, 10).map((er, i) => (
                      <li key={`err-${i}`}>Linha {er.row}: {er.error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {result.details?.skipped?.length > 0 && (
                <div className="text-xs">
                  <p className="font-semibold text-amber-700 mt-2">Puladas:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    {result.details.skipped.slice(0, 10).map((sk, i) => (
                      <li key={`sk-${i}`}>Linha {sk.row}: {sk.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t">
            <Button variant="outline" onClick={handleClose}>Fechar</Button>
            <Button
              onClick={handleImport}
              disabled={importing || !csvText.trim()}
              className="bg-red-700 hover:bg-red-800"
              data-testid="btn-confirm-import-expenses"
            >
              {importing ? 'Importando...' : 'Importar Despesas'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
